import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2, Droplets, Zap, Wifi, Trash, Car, Shield, GripVertical, Tag } from 'lucide-react'
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from './Button'
import { Input } from './Input'
import { Select } from './Select'
import type { PricingFormula, PricingItem } from '@/types'
import { WIZARD_DEFAULT_PRICING_FORMULA } from '@/lib/wizardDefaults'
import { DiscountFormulaItemEditor } from '@/components/pricing/DiscountFormulaItemEditor'

export type { PricingItem }

export interface PricingFormulaEditorProps {
  value: PricingFormula
  onChange: (value: PricingFormula) => void
}

// ---------------------------------------------------------------------------
// EP-114 T014/T015/T016 (S2-11 / Q-10 / D-6) — drag-and-drop reordering
//
// The two stacked ghost move-up/move-down buttons are replaced by a SINGLE
// 24px grip handle, per the Figma. Those buttons were keyboard-operable, so
// the replacement ships pointer + keyboard + touch reordering as ONE unit
// (D-6) — a pointer-only handle would be a straight a11y regression.
//
// Sensor tuning (T016). @dnd-kit's PointerSensor unifies mouse and touch
// behind one activation constraint, but the two input types want DIFFERENT
// constraints, so they are registered separately:
//
//  - MouseSensor  { distance: 8 } — an 8px travel threshold means a plain
//    click on the handle (to focus it) or a few pixels of hand jitter never
//    starts a drag, while a deliberate drag begins almost immediately.
//
//  - TouchSensor  { delay: 200, tolerance: 8 } — this is the drag-vs-scroll
//    fix. The pricing list lives inside a vertically scrolling page, so a
//    finger moving down the screen is ambiguous. With a delay constraint,
//    @dnd-kit cancels the pending drag as soon as the finger travels more
//    than 8px BEFORE the 200ms elapses (core.esm.js `handleMove` →
//    `hasExceededDistance(delta, tolerance)` → `handleCancel`), so a flick
//    scrolls the page normally. Press and hold for 200ms and the drag
//    activates instead; from that point `handleMove` calls
//    `event.preventDefault()` on every cancelable touchmove, so the page
//    stops scrolling and the gesture belongs to the drag.
//
//  - KeyboardSensor + sortableKeyboardCoordinates — Space/Enter picks up,
//    arrow keys move, Space/Enter drops, Escape cancels.
//
// The handle carries `touch-manipulation` (NOT `touch-action: none`).
// `none` would make the 24px strip un-scrollable on touch, which is exactly
// what the delay constraint already solves more gracefully; `manipulation`
// keeps panning available pre-activation and still kills double-tap zoom.
// ---------------------------------------------------------------------------

const MOUSE_ACTIVATION_DISTANCE_PX = 8
const TOUCH_ACTIVATION_DELAY_MS = 200
const TOUCH_ACTIVATION_TOLERANCE_PX = 8

interface SortablePricingCardProps {
  /** Sortable id — the pricing item's `key` (already unique per formula). */
  id: string
  testId: string
  className: string
  /** Accessible name for the grip handle, e.g. "Reorder Water, item 2 of 4". */
  dragHandleLabel: string
  /** Localised value for the handle's `aria-roledescription`. */
  roleDescription: string
  /**
   * Render prop: receives the ready-made grip handle so the caller keeps
   * ownership of the card body and can place the handle inside its own
   * header row. Avoids forking the card markup out of the editor.
   */
  children: (dragHandle: ReactNode) => ReactNode
}

function SortablePricingCard({
  id,
  testId,
  className,
  dragHandleLabel,
  roleDescription,
  children,
}: SortablePricingCardProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id, attributes: { roleDescription } })

  // Translate only (never CSS.Transform, which also emits scaleX/scaleY):
  // the cards have different heights depending on the pricing type, and a
  // scale would visibly distort the taller "metered" card mid-drag.
  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition,
  }

  const dragHandle = (
    <Button
      type="button"
      ref={setActivatorNodeRef}
      variant="ghost"
      size="xs"
      // 24px wide (the Figma specs the glyph at 24px, and this keeps the
      // card's left rhythm) × 32px tall, so the target clears WCAG 2.2
      // SC 2.5.8's 24×24 minimum with headroom on the drag axis.
      className="p-0 min-h-0 h-8 w-6 shrink-0 cursor-grab active:cursor-grabbing touch-manipulation"
      aria-label={dragHandleLabel}
      data-testid={`pricing-card-drag-handle-${id}`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="w-6 h-6" aria-hidden="true" />
    </Button>
  )

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={testId}
      className={`${className}${isDragging ? ' relative z-10 opacity-60 shadow-md' : ''}`}
    >
      {children(dragHandle)}
    </div>
  )
}

// Preset definitions — only structural data; names resolved via i18n at render time.
const presetTypes = [
  { key: 'water', icon: Droplets, type: 'metered' as const, rate: 15, minimum: 100 },
  { key: 'electricity', icon: Zap, type: 'metered' as const, rate: 7 },
  { key: 'common_fee', icon: Shield, type: 'percentage' as const, rate: 3 },
  { key: 'internet', icon: Wifi, type: 'fixed' as const, amount: 0 },
  { key: 'garbage', icon: Trash, type: 'fixed' as const, amount: 0 },
  { key: 'parking', icon: Car, type: 'fixed' as const, amount: 0 },
]

export function PricingFormulaEditor({ value, onChange }: PricingFormulaEditorProps) {
  const { t, i18n } = useTranslation('properties')

  // WEB-M13: preset keys are DYNAMIC (operator-defined custom items have no
  // locale entry), so a blind t() would render the raw key and a defaultValue
  // fallback violates the no-fallback i18n rule. Branch on i18n.exists():
  // known presets translate; custom items keep their stored name.
  const presetLabel = (key: string, fallback: string, variant: 'name' | 'name_long' = 'name') => {
    const fullKey = `pricing_editor.presets.${key}.${variant}`
    return i18n.exists(`properties:${fullKey}`) ? t(fullKey) : fallback
  }
  const { t: tc } = useTranslation('common')

  const [showAddForm, setShowAddForm] = useState(false)
  const [newItemName, setNewItemName] = useState('')

  // Unit and type options — labels resolved from i18n at render time.
  const unitOptions = [
    { value: 'unit', label: t('pricing_editor.units.unit') },
    { value: 'kWh', label: t('pricing_editor.units.kwh') },
    { value: 'cubic_meter', label: t('pricing_editor.units.cubic_meter') },
  ]

  const typeOptions = [
    { value: 'metered', label: t('pricing_editor.types.metered') },
    { value: 'fixed', label: t('pricing_editor.types.fixed') },
    { value: 'percentage', label: t('pricing_editor.types.percentage') },
    { value: 'per_sqm', label: t('pricing_editor.types.per_sqm') },
    { value: 'per_share_ratio', label: t('pricing_editor.types.per_share_ratio') },
    { value: 'discount', label: tc('pricing_formula.types.discount') },
  ]

  // Ensure value is an array
  const items: PricingItem[] = Array.isArray(value) ? value : []

  // Resolves i18n names from WIZARD_DEFAULT_PRICING_FORMULA at render-time.
  // Returns a new mutable array (the constant is frozen).
  const seedFromDefaults = (): PricingItem[] =>
    WIZARD_DEFAULT_PRICING_FORMULA.map((item) => ({
      ...item,
      name: presetLabel(item.key, item.name),
    }))

  const updateItem = (index: number, updates: Partial<PricingItem>) => {
    const newItems = [...items]
    newItems[index] = { ...newItems[index], ...updates }
    onChange(newItems)
  }

  const removeItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index)
    onChange(newItems)
  }

  // Array-mutation primitive shared by BOTH reorder paths (pointer drag and
  // keyboard drag) — they converge on @dnd-kit's single onDragEnd, so the
  // resulting onChange payload is identical by construction, not by luck.
  // Unchanged from the move-up/move-down era: same splice semantics, same
  // `onChange(PricingItem[])` contract, no data-shape change.
  const moveItem = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= items.length) return
    const newItems = [...items]
    const [removed] = newItems.splice(fromIndex, 1)
    newItems.splice(toIndex, 0, removed)
    onChange(newItems)
  }

  // ---- Drag and drop (EP-114 T014/T015/T016) ------------------------------

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: MOUSE_ACTIVATION_DISTANCE_PX },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_ACTIVATION_DELAY_MS,
        tolerance: TOUCH_ACTIVATION_TOLERANCE_PX,
      },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const sortableIds = items.map((item) => item.key)

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const fromIndex = items.findIndex((item) => item.key === active.id)
    const toIndex = items.findIndex((item) => item.key === over.id)
    if (fromIndex === -1 || toIndex === -1) return
    moveItem(fromIndex, toIndex)
  }

  // Screen-reader announcements. @dnd-kit renders these into its own
  // aria-live region, so a keyboard/SR user hears pick-up, every position
  // change and the drop/cancel. Positions are 1-based against the CURRENT
  // array (announcements fire before onChange lands).
  const describe = (id: UniqueIdentifier | undefined) => {
    const index = items.findIndex((item) => item.key === id)
    return {
      name: index === -1 ? '' : items[index].name,
      position: index + 1,
      total: items.length,
    }
  }

  const announcements: Announcements = useMemo(
    () => ({
      onDragStart: ({ active }) => t('pricing_editor.dnd.picked_up', describe(active.id)),
      // `undefined` tells @dnd-kit to leave the live region alone. Returning a
      // string when `over` IS the dragged item would announce "moved to
      // position 1 of 3" the instant it is picked up (nothing moved) — and
      // because React batches that with the pick-up announcement in the same
      // commit, it would also OVERWRITE "Picked up …" before a screen reader
      // ever sees it. Verified against @dnd-kit 6.3.1, not assumed.
      onDragOver: ({ active, over }) =>
        over && over.id !== active.id
          ? t('pricing_editor.dnd.moved_over', {
              ...describe(over.id),
              name: describe(active.id).name,
            })
          : undefined,
      onDragEnd: ({ active, over }) =>
        over
          ? t('pricing_editor.dnd.dropped', {
              ...describe(over.id),
              name: describe(active.id).name,
            })
          : t('pricing_editor.dnd.cancelled', describe(active.id)),
      onDragCancel: ({ active }) => t('pricing_editor.dnd.cancelled', describe(active.id)),
    }),
    // `describe` closes over `items`; recreate the handlers when either the
    // list or the active locale changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, t],
  )

  const screenReaderInstructions: ScreenReaderInstructions = useMemo(
    () => ({ draggable: t('pricing_editor.dnd.instructions') }),
    [t],
  )

  const addPreset = (preset: (typeof presetTypes)[0]) => {
    if (items.find((item) => item.key === preset.key)) return // Already exists
    // Resolve the i18n name for the preset key to store a localised name.
    // Fall back to the key itself if no translation is found.
    const presetName = presetLabel(preset.key, preset.key)
    const newItem: PricingItem = {
      key: preset.key,
      name: presetName,
      type: preset.type,
      ...(preset.type === 'metered' && { rate: preset.rate, minimum: preset.minimum }),
      ...(preset.type === 'fixed' && { amount: preset.amount }),
      ...(preset.type === 'percentage' && { rate: preset.rate }),
    }
    onChange([...items, newItem])
  }

  const addCustomItem = () => {
    if (!newItemName.trim()) return
    const key = newItemName.toLowerCase().replace(/\s+/g, '_')
    if (items.find((item) => item.key === key)) return // Already exists
    const newItem: PricingItem = {
      key,
      name: newItemName.trim(),
      type: 'fixed',
      amount: 0,
    }
    onChange([...items, newItem])
    setNewItemName('')
    setShowAddForm(false)
  }

  // EP-114 T013 S2-10 — the card-cal icon chip shrunk from 40px to 32px, so
  // the glyph inside drops from 20px (w-5 h-5) to 16px (w-4 h-4) to match.
  const getIcon = (key: string, type?: string) => {
    if (type === 'discount') return <Tag className="w-4 h-4 text-warning" />
    const preset = presetTypes.find((p) => p.key === key)
    if (preset) {
      const Icon = preset.icon
      return <Icon className="w-4 h-4" />
    }
    return <Zap className="w-4 h-4" />
  }

  // Available presets that haven't been added yet
  const availablePresets = presetTypes.filter((preset) => !items.find((item) => item.key === preset.key))

  return (
    <div className="space-y-4">
      {/* Quick Add Presets — EP-114 S2-4/D-3: rendered ABOVE the card list
          (document order only, no position: sticky anywhere on this page —
          owner-confirmed Q-12). Label is 13px SemiBold text-primary (S2-5,
          exact #004C50 token match); chips use the T001 fd-outline-secondary
          Button variant (S2-6, exact #6B6561/base-content token match) instead
          of the primary-tinted "outline" literal. */}
      {availablePresets.length > 0 && (
        // No bottom rule. Figma's `add-more-section` (183:1972) is one 104px
        // frame — label at y=0, chips at y=28, the custom-item button at y=72 —
        // with no divider between the chips and that button. The old
        // `border-b border-base-200 pb-4` drew one. Spacing is left to the
        // parent's `space-y-4`.
        <div>
          <p className="text-[13px] font-semibold text-primary mb-2">{t('pricing_editor.quick_add')}</p>
          <div className="flex flex-wrap gap-2">
            {availablePresets.map((preset) => {
              const Icon = preset.icon
              const presetName = presetLabel(preset.key, preset.key)
              return (
                <Button
                  key={preset.key}
                  type="button"
                  size="sm"
                  variant="fd-outline-secondary"
                  onClick={() => addPreset(preset)}
                  leftIcon={<Icon className="w-4 h-4" />}
                >
                  {presetName}
                </Button>
              )
            })}
          </div>
        </div>
      )}

      {/* Add Custom Item. Figma 183:2308 is `btn-outline-primary-sm`, so this is
          `variant="primary"` + `outline` (renders `btn-primary btn-outline`).

          CORRECTION (2026-08-04): S2-7 previously claimed the bare
          `variant="outline"` "already closely matches the Figma spec
          (border-[#004C50]/text-[#004C50]) — verified, no change needed". That
          was wrong. `variant="outline"` maps to a bare `btn-outline`, and
          DaisyUI leaves that neutral because it needs a colour to outline, so
          the button rendered base-content grey — identical to the secondary
          chips above it. Measured live: text and border both
          oklch(0.51 0.01 55), where --color-primary is oklch(37.67% 0.072 199.9).
          Caught by the owner spotting it visually. */}
      {showAddForm ? (
        <div className="border border-base-300 rounded-lg p-4 space-y-3">
          <Input
            label={t('pricing_editor.custom_item_label')}
            placeholder={t('pricing_editor.custom_item_placeholder')}
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
          />
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={addCustomItem} disabled={!newItemName.trim()}>
              {t('pricing_editor.add_item_button')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowAddForm(false)
                setNewItemName('')
              }}
            >
              {tc('actions.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="button"
          variant="primary"
          outline
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowAddForm(true)}
        >
          {t('pricing_editor.add_custom')}
        </Button>
      )}

      {/* Current Items */}
      {items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{ announcements, screenReaderInstructions }}
        >
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
          {/* EP-108 owner review r2: step cards are WHITE with a border (was gray
              bg-base-200) — they sit on white Card surfaces app-wide, and the
              nested-card look reads better than a gray slab. The icon circle inside
              inverts to bg-base-200 so it stays visible on the white card.
              EP-114 T013 S2-9 — card-cal geometry: desktop rounded-xl (12px)
              p-4 gap-4; mobile keeps rounded-lg (8px) but with an asymmetric
              pl-2/pr-4/py-3 + tighter gap-2 (a responsive split, not one flat
              value). The card is now `flex flex-col` so `gap-*` drives the
              vertical spacing between the header row and the stacked-fields
              row (replaces the old flat `mt-3` on the fields row below). */}
          {items.map((item, index) => (
            <SortablePricingCard
              key={item.key}
              id={item.key}
              testId={`pricing-card-cal-${item.key}`}
              className="flex flex-col gap-2 pl-2 pr-4 py-3 rounded-lg bg-base-100 border border-base-300 sm:gap-4 sm:p-4 sm:rounded-xl"
              dragHandleLabel={t('pricing_editor.aria.drag_handle', {
                name: item.name,
                position: index + 1,
                total: items.length,
              })}
              roleDescription={t('pricing_editor.dnd.role_description')}
            >
              {(dragHandle) => (
                <>
              {/* Top row: drag handle | icon | name | delete.
                  Name owns this row by itself — type select + per-type inputs
                  drop into stacked rows below (one field per line, per user
                  request). Name no longer competes with the type select for
                  column width. */}
              <div className="flex items-start gap-3">
                {/* EP-114 T014 S2-11 — a SINGLE 24px grip handle replaces the
                    stacked move-up/move-down pair. It is the @dnd-kit
                    activator for all three sensors (mouse, touch, keyboard),
                    so pointer drag and keyboard reorder funnel through the
                    same onDragEnd → moveItem path. */}
                {dragHandle}
                {/* EP-114 T013 S2-10 — icon chip: 32px, bg-fd-green-light token
                    (was bg-base-200), rounded-2xl (16px, was rounded-full). */}
                <div className="w-8 h-8 rounded-2xl bg-fd-green-light flex items-center justify-center text-primary shrink-0">
                  {getIcon(item.key, item.type)}
                </div>
                <div
                  className="font-medium flex-1 min-w-0 break-words leading-snug pt-2"
                  title={presetLabel(item.key, item.name, 'name_long')}
                >
                  {item.name || presetLabel(item.key, item.key)}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-error shrink-0"
                  onClick={() => removeItem(index)}
                  aria-label={t('pricing_editor.aria.remove', { name: item.name || presetLabel(item.key, item.key) })}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Stacked form rows — one field per line, indented to align
                  under the title. The suffix label ("ต่อเดือน" / "ของยอดรวม"
                  / ฯลฯ) rides inline with its companion input because it
                  semantically modifies that value. ml-[68px] = handle (24 —
                  EP-114 T014 replaced the 16px stacked move buttons with one
                  24px grip, was 16) + gap (12) + icon (32 — EP-114 T013 shrunk
                  the chip 40px→32px). Each Input is wrapped in a sized div so
                  the form-control w-full wrapper doesn't stretch the column.
                  overflow-x-hidden prevents the indent from blowing out the
                  parent card on narrow (< 390px) viewports. */}
              {/* EP-108 owner review r2: mr-11 right indent — the field column stops
                  short of the delete-button column, keeping white space under the
                  delete icon (mirrors the ml-[68px] left indent under the handle+icon). */}
              {/* Mobile: fields span the full card width (no left/right indent) so
                  the amount value isn't squeezed on narrow phones (iPhone 15 Pro
                  clipped "5000"). Desktop keeps the indent under the handle+icon
                  and short of the delete column. */}
              <div className="sm:ml-[68px] sm:mr-11 space-y-2 overflow-x-hidden">
                {/* Type select — always the first stacked row */}
                <Select
                  value={item.type}
                  onChange={(e) => {
                    const newType = e.target.value as PricingItem['type']
                    const updates: Partial<PricingItem> = { type: newType }
                    if (newType === 'metered') {
                      updates.rate = item.rate ?? item.amount ?? 0
                      updates.unit = item.unit || 'unit'
                      delete updates.amount
                    } else if (newType === 'fixed') {
                      updates.amount = item.amount ?? item.rate ?? 0
                      delete updates.rate
                      delete updates.unit
                      delete updates.minimum
                    } else if (newType === 'percentage' || newType === 'per_sqm' || newType === 'per_share_ratio') {
                      updates.rate = item.rate ?? 0
                      delete updates.amount
                      delete updates.unit
                      delete updates.minimum
                    } else if (newType === 'discount') {
                      updates.amount = item.amount ?? 0
                      delete updates.rate
                      delete updates.unit
                      delete updates.minimum
                    }
                    updateItem(index, updates)
                  }}
                  options={typeOptions}
                  className="w-full"
                  aria-label={t('pricing_editor.aria.type', { name: item.name })}
                />

                {item.type === 'metered' && (
                  <>
                    <div className="w-full">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('pricing.rate')}
                        value={item.rate ?? ''}
                        onChange={(e) => updateItem(index, { rate: Number(e.target.value) })}
                        leftIcon={<span className="text-xs">฿</span>}
                        aria-label={t('pricing_editor.aria.rate', { name: item.name })}
                      />
                    </div>
                    <Select
                      value={item.unit || 'unit'}
                      onChange={(e) => updateItem(index, { unit: e.target.value })}
                      options={unitOptions}
                      className="w-full"
                      aria-label={t('pricing_editor.aria.unit', { name: item.name })}
                    />
                    <div className="w-full">
                      <Input
                        type="number"
                        placeholder={t('pricing_editor.placeholders.minimum')}
                        value={item.minimum || ''}
                        onChange={(e) =>
                          updateItem(index, { minimum: e.target.value ? Number(e.target.value) : undefined })
                        }
                        leftIcon={<span className="text-xs">฿</span>}
                        aria-label={t('pricing_editor.aria.minimum', { name: item.name })}
                      />
                    </div>
                  </>
                )}
                {item.type === 'fixed' && (
                  <div className="w-full">
                    <div>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('pricing.amount')}
                        value={item.amount ?? ''}
                        onChange={(e) => updateItem(index, { amount: Number(e.target.value) })}
                        leftIcon={<span className="text-xs">฿</span>}
                        aria-label={t('pricing_editor.aria.amount', { name: item.name })}
                        rightAddon={t('pricing_editor.per_month')}
                        inputClassName="pr-24"
                      />
                    </div>
                  </div>
                )}
                {item.type === 'percentage' && (
                  <div className="w-full">
                    <div>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder={t('pricing.rate')}
                        value={item.rate ?? ''}
                        onChange={(e) => updateItem(index, { rate: Number(e.target.value) })}
                        leftIcon={<span className="text-xs">%</span>}
                        aria-label={t('pricing_editor.aria.percentage', { name: item.name })}
                        rightAddon={t('pricing_editor.of_total')}
                        inputClassName="pr-28"
                      />
                    </div>
                  </div>
                )}
                {item.type === 'per_sqm' && (
                  <div className="w-full">
                    <div>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder={t('pricing.rate')}
                        value={item.rate ?? ''}
                        onChange={(e) => updateItem(index, { rate: Number(e.target.value) })}
                        leftIcon={<span className="text-xs">฿</span>}
                        aria-label={t('pricing_editor.aria.per_sqm', { name: item.name })}
                        rightAddon={t('pricing_editor.per_sqm_unit')}
                        inputClassName="pr-24"
                      />
                    </div>
                  </div>
                )}
                {item.type === 'per_share_ratio' && (
                  <div className="w-full">
                    <div>
                      <Input
                        type="number"
                        step="1"
                        placeholder={t('pricing.rate')}
                        value={item.rate ?? ''}
                        onChange={(e) => updateItem(index, { rate: Number(e.target.value) })}
                        leftIcon={<span className="text-xs">฿</span>}
                        aria-label={t('pricing_editor.aria.per_share_ratio', { name: item.name })}
                        rightAddon={t('pricing_editor.per_share_ratio_unit')}
                        inputClassName="pr-56"
                      />
                    </div>
                  </div>
                )}
                {item.type === 'discount' && (
                  <DiscountFormulaItemEditor
                    value={{ key: item.key, label: item.name, type: 'discount', amount: item.amount ?? 0 }}
                    onChange={(updated) => updateItem(index, { key: updated.key, name: updated.label, amount: updated.amount })}
                    siblingKeys={items
                      .filter((it, i) => it.type === 'discount' && i !== index)
                      .map((it) => it.key)}
                  />
                )}
              </div>
                </>
              )}
            </SortablePricingCard>
          ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="text-center py-8 space-y-3" data-testid="pricing-editor-empty-state">
          <p className="text-base-content/60">{t('pricing_editor.empty_state')}</p>
          <div className="flex flex-col gap-2 max-w-xs mx-auto">
            <Button
              type="button"
              variant="primary"
              onClick={() => onChange(seedFromDefaults())}
              data-testid="pricing-editor-seed-defaults"
            >
              {t('pricing_editor.cta.add_defaults')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowAddForm(true)}
              data-testid="pricing-editor-add-custom"
            >
              {t('pricing_editor.cta.add_custom')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
